import React, { useState } from 'react';
import {
    ChevronDownIcon,
    ChevronRightIcon,
    Edit,
    Trash2,
    FileText,
    Link as LinkIcon,
    Download,
    ExternalLink,
    Calendar
} from 'lucide-react';

const MentorLessonCard = ({
    lesson,
    isExpanded,
    onToggle,
    onEdit,
    onDelete,
    showActions = true,
    isDeleting = false,
    isEditing: isEditingFromParent = false
}) => {
    const [isEditing, setIsEditing] = useState(false);
    const [editData, setEditData] = useState({
        title: lesson.title,
        description: lesson.description,
        type: lesson.type
    });

    const handleSaveEdit = async () => {
        await onEdit(lesson.id, editData);
        setIsEditing(false);
    };

    const handleCancelEdit = () => {
        setEditData({
            title: lesson.title,
            description: lesson.description,
            type: lesson.type
        });
        setIsEditing(false);
    };

    const getTypeIcon = (type) => {
        switch (type) {
            case 'event':
                return <Calendar className="w-4 h-4" />;
            case 'link':
                return <LinkIcon className="w-4 h-4" />;
            case 'document':
                return <FileText className="w-4 h-4" />;
            default:
                return <FileText className="w-4 h-4" />;
        }
    };

    const getTypeColor = (type) => {
        switch (type) {
            case 'event':
                return 'bg-purple-100 text-purple-800';
            case 'video':
                return 'bg-red-100 text-red-800';
            case 'link':
                return 'bg-blue-100 text-blue-800';
            case 'pdf':
            case 'document':
                return 'bg-green-100 text-green-800';
            default:
                return 'bg-gray-100 text-gray-800';
        }
    };

    const formatDate = (timestamp) => {
        if (!timestamp) return "N/A";

        let date;

        if (timestamp.toDate) {
            // Firestore Timestamp object
            date = timestamp.toDate();
        } else if (typeof timestamp.seconds === "number") {
            // Plain object from Firestore (seconds + nanoseconds)
            date = new Date(timestamp.seconds * 1000);
        } else {
            // Try to construct Date normally
            date = new Date(timestamp);
        }

        if (isNaN(date.getTime())) return "N/A";

        return date.toLocaleDateString("en-US", {
            year: "numeric",
            month: "short",
            day: "numeric",
        });
    };

    return (
        <div className="border border-gray-200 rounded-lg bg-white hover:shadow-md transition-shadow">
            {/* Card Header */}
            <div className="p-4">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3 flex-1">
                        <button
                            onClick={onToggle}
                            className="text-gray-400 hover:text-gray-600 transition-colors"
                        >
                            {isExpanded ? (
                                <ChevronDownIcon className="w-5 h-5" />
                            ) : (
                                <ChevronRightIcon className="w-5 h-5" />
                            )}
                        </button>

                        <div className={`p-2 rounded-lg ${getTypeColor(lesson.type)}`}>
                            {getTypeIcon(lesson.type)}
                        </div>

                        <div className="flex-1 min-w-0">
                            {isEditing ? (
                                <input
                                    type="text"
                                    value={editData.title}
                                    onChange={(e) => setEditData({ ...editData, title: e.target.value })}
                                    className="w-full font-semibold text-gray-900 bg-transparent border-b border-gray-300 focus:border-custom-blue-1 focus:outline-none"
                                />
                            ) : (
                                <h4 className="font-semibold text-gray-900 truncate">{lesson.title}</h4>
                            )}
                            <div className="flex items-center gap-2 mt-1">
                                <span className="text-sm text-gray-500">by {lesson.instructor}</span>
                                {lesson.uploadedAt && (
                                    <>
                                        <span className="text-gray-300">•</span>
                                        <span className="text-sm text-gray-500">{formatDate(lesson.uploadedAt)}</span>
                                    </>
                                )}
                            </div>
                        </div>
                    </div>

                    {showActions && (
                        <div className="flex items-center gap-2">
                            {isEditing ? (
                                <>
                                    <button
                                        onClick={handleSaveEdit}
                                        disabled={isEditingFromParent}
                                        className={`p-1 transition-colors flex items-center gap-1 ${isEditingFromParent
                                                ? 'text-gray-400 cursor-not-allowed'
                                                : 'text-green-600 hover:text-green-800'
                                            }`}
                                        title="Save changes"
                                    >
                                        {isEditingFromParent ? (
                                            <div className="w-4 h-4 border-2 border-green-600 border-t-transparent rounded-full animate-spin"></div>
                                        ) : null}
                                        Save
                                    </button>
                                    <button
                                        onClick={handleCancelEdit}
                                        disabled={isEditingFromParent}
                                        className={`p-1 transition-colors ${isEditingFromParent
                                                ? 'text-gray-400 cursor-not-allowed'
                                                : 'text-gray-600 hover:text-gray-800'
                                            }`}
                                        title="Cancel editing"
                                    >
                                        Cancel
                                    </button>
                                </>
                            ) : (
                                <>
                                    <button
                                        onClick={() => setIsEditing(true)}
                                        disabled={isEditingFromParent || isDeleting}
                                        className={`p-1 transition-colors ${isEditingFromParent || isDeleting
                                                ? 'text-gray-400 cursor-not-allowed'
                                                : 'text-gray-600 hover:text-blue-600'
                                            }`}
                                        title="Edit resource"
                                    >
                                        <Edit className="w-4 h-4" />
                                    </button>
                                    <button
                                        onClick={() => onDelete(lesson.id)}
                                        disabled={isEditingFromParent || isDeleting}
                                        className={`p-1 transition-colors flex items-center gap-1 ${isEditingFromParent || isDeleting
                                                ? 'text-gray-400 cursor-not-allowed'
                                                : 'text-gray-600 hover:text-red-600'
                                            }`}
                                        title="Delete resource"
                                    >
                                        {isDeleting ? (
                                            <div className="w-4 h-4 border-2 border-red-600 border-t-transparent rounded-full animate-spin"></div>
                                        ) : (
                                            <Trash2 className="w-4 h-4" />
                                        )}
                                    </button>
                                </>
                            )}
                        </div>
                    )}
                </div>
            </div>

            {/* Expanded Content */}
            {isExpanded && (
                <div className="px-4 pb-4 border-t border-gray-100">
                    <div className="pt-4">
                        {isEditing ? (
                            <div className="space-y-3">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Description
                                    </label>
                                    <textarea
                                        value={editData.description}
                                        onChange={(e) => setEditData({ ...editData, description: e.target.value })}
                                        className="w-full p-2 border border-gray-300 rounded-md focus:ring-custom-blue-1 focus:border-custom-blue-1"
                                        rows="3"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Type
                                    </label>
                                    <select
                                        value={editData.type}
                                        onChange={(e) => setEditData({ ...editData, type: e.target.value })}
                                        className="w-full p-2 border border-gray-300 rounded-md focus:ring-custom-blue-1 focus:border-custom-blue-1"
                                    >
                                        <option value="event">Event</option>
                                        <option value="document">Document</option>
                                        <option value="video">Video</option>
                                        <option value="link">Link</option>
                                        <option value="pdf">PDF</option>
                                    </select>
                                </div>
                            </div>
                        ) : (
                            <>
                                <p className="text-gray-600 mb-4">{lesson.description}</p>

                                {/* Resource Access */}
                                <div className="flex items-center gap-3">
                                    {lesson.type === 'event' && lesson.eventUrl && (
                                        <a
                                            href={lesson.eventUrl}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="flex items-center gap-2 px-3 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 transition-colors text-sm"
                                        >
                                            <Calendar className="w-4 h-4" />
                                            View Event Details
                                        </a>
                                    )}

                                    {lesson.fileUrl && (
                                        <a
                                            href={lesson.fileUrl}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="flex items-center gap-2 px-3 py-2 bg-custom-blue-1 text-white rounded-md hover:bg-blue-700 transition-colors text-sm"
                                        >
                                            <Download className="w-4 h-4" />
                                            Download
                                        </a>
                                    )}

                                    {lesson.resourceUrl && (
                                        <a
                                            href={lesson.resourceUrl}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="flex items-center gap-2 px-3 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors text-sm"
                                        >
                                            <ExternalLink className="w-4 h-4" />
                                            View Resource
                                        </a>
                                    )}
                                </div>
                            </>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default MentorLessonCard;
